import { ref } from 'vue'

export type Locale = 'zh' | 'en'

const STORAGE_KEY = 'openaaas-locale'

function isLocale(value: string): value is Locale {
  return value === 'zh' || value === 'en'
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current === null || current === undefined) return undefined
    return (current as Record<string, unknown>)[key]
  }, obj)
}

const messages: Record<Locale, Record<string, unknown>> = {
  zh: {
    nav: {
      painPoints: '痛点',
      what: '概览',
      how: '工作原理',
      components: '组件',
      getStarted: '快速开始',
      download: '下载',
      github: 'GitHub',
      menu: '菜单',
      toggleLang: 'English',
    },
    hero: {
      title: 'OpenAaaS — Open Us to the Agentic World',
      subtitle:
        '一个开放的 Agent-to-Agent 编排网络：任何 Agent 都可以发现、委派并组合运行在远程节点上的其他完整 Agent 实例。',
      slogan: '智能流动，数据静止',
      github: 'GitHub',
      releases: 'Releases',
      binder: 'Binder',
      scroll: '向下滚动',
      logoAlt: 'OpenAaaS Logo',
    },
    painPoints: {
      title: '为什么需要 OpenAaaS？',
      subtitle:
        '在 AI for Science 落地过程中，数据、工具与网络环境往往成为 Agent 能力发挥的三重瓶颈。',
      items: [
        {
          icon: 'lock',
          title: '数据锁在机房',
          subtitle: '数据不出域',
          description:
            '科研数据、实验记录、患者隐私受合规约束，无法上传到外部 SaaS，传统 Agent 只能“望数兴叹”。',
        },
        {
          icon: 'link-slash',
          title: '文献与工具分散',
          subtitle: 'Agent 能力孤立',
          description:
            '每个模型、插件、数据库都各自为战，缺乏统一协议让 Agent 之间互相发现、委派与组合。',
        },
        {
          icon: 'firewall',
          title: '接入门槛高',
          subtitle: '防火墙与 NAT 限制',
          description:
            '实验室环境只有单向出站、没有公网 IP，常规远程调用方案需要开放端口或 SSH，部署举步维艰。',
        },
      ],
    },
    what: {
      title: 'OpenAaaS 是什么？',
      subtitle:
        'OpenAaaS 是一个面向 AI for Science 的 Agent-to-Agent 编排网络，让 Agent 走到数据身边，而不是把数据交给 Agent。',
      items: [
        {
          icon: 'database',
          title: '数据原地处理',
          description:
            '原始数据始终留在产生它的位置，远程 Agent 直接在数据身旁工作，无需迁移 TB 级数据。',
        },
        {
          icon: 'shield',
          title: '单向出站入网',
          description:
            '节点只需出站 HTTP 即可加入网络，无需公网 IP、无需开放入站端口、无需 SSH，专为实验室防火墙与 NAT 环境设计。',
        },
        {
          icon: 'box',
          title: '完整 Agent 实例编排',
          description:
            '编排的是运行在远程节点上的完整 Agent 实例，而非脚本、函数或固定 API，可自主决策并继续委派子 Agent。',
        },
      ],
    },
    how: {
      title: '工作原理',
      subtitle: '控制流与委派请求在网络中流动，原始数据始终停留在本地节点。',
      diagram: {
        client: '客户端 Agent',
        clientDesc1: 'Claude / pi / Kimi',
        clientDesc2: 'Codex / 自研 Agent',
        server: 'OpenAaaS Server',
        serverDesc1: 'Rust + SQLite',
        serverDesc2: '网络枢纽',
        core: 'Agent Core',
        coreDesc1: 'Rust + Docker',
        coreDesc2: '数据本地节点',
        data: '本地数据',
        dataDesc: 'TB 级原始数据',
        labelRequest: '委派请求',
        labelRoute: '路由',
        labelLocal: '本地执行',
      },
      steps: [
        '客户端 Agent 向 Server 注册并发现远程节点',
        'Server 将委派请求路由到合适的 Agent Core',
        'Agent Core 在本地启动完整 Agent 实例处理数据',
      ],
      ariaLabel: 'OpenAaaS 架构图',
    },
    components: {
      title: '组件',
      subtitle: '从网络枢纽到客户端扩展，完整覆盖 Agent-to-Agent 编排的每一层。',
      items: [
        {
          icon: 'server',
          title: 'Server',
          subtitle: '网络枢纽',
          description: 'Rust + SQLite 轻量索引层，负责节点注册、委派路由、心跳与文件中转。',
        },
        {
          icon: 'cpu',
          title: 'Agent Core',
          subtitle: '网络节点',
          description: 'Rust + Docker 部署在数据本地，运行完整 Agent 实例并隔离执行任务。',
        },
        {
          icon: 'code',
          title: 'Python SDK',
          subtitle: 'pyopenaaas',
          description: 'pip 安装即可接入网络，注册、发现节点、提交委派任务、下载结果。',
        },
        {
          icon: 'monitor',
          title: '桌面客户端',
          subtitle: 'Tauri + Vue 3',
          description: '跨平台桌面应用，管理多服务器、拖拽上传、实时查看任务进度。',
        },
        {
          icon: 'mcp',
          title: 'MCP 适配器',
          subtitle: 'openaaas-mcp-adapter',
          description: '已发布 PyPI，Claude / Cursor / Cline 等 MCP 客户端一条配置即可接入。',
        },
        {
          icon: 'pi',
          title: 'pi 插件',
          subtitle: 'pi extension',
          description: '在 pi 对话中直接完成注册、节点发现、任务委派与结果获取。',
        },
      ],
    },
    getStarted: {
      title: '快速开始',
      subtitle: '选择适合你的接入方式，五分钟内让 Agent 加入 OpenAaaS 网络。',
      tabs: {
        mcp: 'MCP',
        pi: 'pi 插件',
        python: 'Python SDK',
        desktop: '桌面客户端',
      },
      snippets: {
        mcp: `{
  "mcpServers": {
    "openaaas": {
      "command": "uvx",
      "args": ["openaaas-mcp-adapter"],
      "toolTimeoutMs": 600000
    }
  }
}`,
        pi: `git clone https://github.com/Wolido/OpenAaaS.git
cd OpenAaaS
mkdir -p ~/.pi/agent/extensions/OpenAaaS
cp -r client-extension/pi-extension/* ~/.pi/agent/extensions/OpenAaaS/
cd ~/.pi/agent/extensions/OpenAaaS
npm install
# 然后在 pi 中执行 /reload`,
        python: `pip install pyopenaaas

import pyopenaaas

client = pyopenaaas.Client()
client.register(name="your-name")

agents = client.list_services()
task = client.submit_task(
    service_id=agents[0].id,
    task_prompt="分析本地实验数据并生成报告",
)
task = client.wait_for_task(task.id)
paths = client.download_all_files(task.id)`,
      },
      desktopAlt: '桌面客户端服务市场界面',
      desktopButton: '前往 Releases 下载',
      chatUser: '帮我接入 OpenAaaS',
      chatAgent: '已为你完成服务器配置和节点注册。',
      chatHint: 'pi 会在背后自动调用 OpenAaaS 工具完成配置',
      copy: '复制',
      copied: '已复制',
      copyError: '复制失败',
      ariaCopy: '复制',
      ariaCopied: '已复制',
      ariaCopyError: '复制失败',
    },
    download: {
      title: '下载',
      subtitle: '获取最新桌面客户端与预编译二进制，支持 macOS / Windows / Linux。',
      button: '前往 GitHub Releases',
      note: '桌面客户端提供 .dmg（macOS）与 .msi / .exe（Windows）安装包。',
    },
    footer: {
      links: {
        github: 'GitHub',
        paper: '论文 arXiv',
        pypi: 'PyPI',
        mcp: 'MCP Adapter',
        license: 'MIT License',
      },
      copyright: 'MIT License © IDM Explorer Lab',
      logoAlt: 'OpenAaaS',
    },
  },
  en: {
    nav: {
      painPoints: 'Pain Points',
      what: 'Overview',
      how: 'How it Works',
      components: 'Components',
      getStarted: 'Get Started',
      download: 'Download',
      github: 'GitHub',
      menu: 'Menu',
      toggleLang: '中文',
    },
    hero: {
      title: 'OpenAaaS — Open Us to the Agentic World',
      subtitle:
        'An open Agent-to-Agent orchestration network: any Agent can discover, delegate to, and compose with other full Agent instances running on remote nodes.',
      slogan: 'Intelligence flows, data stays at rest',
      github: 'GitHub',
      releases: 'Releases',
      binder: 'Binder',
      scroll: 'Scroll down',
      logoAlt: 'OpenAaaS Logo',
    },
    painPoints: {
      title: 'Why OpenAaaS?',
      subtitle:
        'In real-world AI for Science deployments, data, tools, and network constraints often become the three bottlenecks limiting Agent capabilities.',
      items: [
        {
          icon: 'lock',
          title: 'Data Locked in the Lab',
          subtitle: 'Data never leaves the domain',
          description:
            'Research data, experiment logs, and patient privacy are bound by compliance. They cannot be uploaded to external SaaS, leaving traditional Agents stranded.',
        },
        {
          icon: 'link-slash',
          title: 'Fragmented Literature & Tools',
          subtitle: 'Agent capabilities are isolated',
          description:
            'Every model, plugin, and database works in isolation. There is no unified protocol for Agents to discover, delegate to, and compose with one another.',
        },
        {
          icon: 'firewall',
          title: 'High Access Barrier',
          subtitle: 'Firewall & NAT limits',
          description:
            'Lab environments often have only outbound HTTP and no public IP. Conventional remote-call solutions require open ports or SSH, making deployment difficult.',
        },
      ],
    },
    what: {
      title: 'What is OpenAaaS?',
      subtitle:
        'OpenAaaS is an Agent-to-Agent orchestration network for AI for Science that brings Agents to the data, instead of sending data to the Agents.',
      items: [
        {
          icon: 'database',
          title: 'Process Data In Situ',
          description:
            'Raw data stays where it is produced. Remote Agents work right next to the data, with no need to move terabytes around.',
        },
        {
          icon: 'shield',
          title: 'Outbound-Only Networking',
          description:
            'Nodes join the network with outbound HTTP only — no public IP, no open inbound ports, no SSH. Built for lab firewalls and NAT environments.',
        },
        {
          icon: 'box',
          title: 'Full Agent Instance Orchestration',
          description:
            'Orchestrate complete Agent instances running on remote nodes, not scripts, functions, or fixed APIs. They can make decisions and delegate to child Agents.',
        },
      ],
    },
    how: {
      title: 'How it Works',
      subtitle: 'Control flow and delegation requests travel through the network while raw data stays on the local node.',
      diagram: {
        client: 'Client Agent',
        clientDesc1: 'Claude / pi / Kimi',
        clientDesc2: 'Codex / custom Agent',
        server: 'OpenAaaS Server',
        serverDesc1: 'Rust + SQLite',
        serverDesc2: 'Network hub',
        core: 'Agent Core',
        coreDesc1: 'Rust + Docker',
        coreDesc2: 'Local data node',
        data: 'Local Data',
        dataDesc: 'TB-scale raw data',
        labelRequest: 'Request',
        labelRoute: 'Route',
        labelLocal: 'Execute',
      },
      steps: [
        'The Client Agent registers with the Server and discovers remote nodes',
        'The Server routes delegation requests to the right Agent Core',
        'The Agent Core launches a full Agent instance locally to process data',
      ],
      ariaLabel: 'OpenAaaS architecture diagram',
    },
    components: {
      title: 'Components',
      subtitle: 'From network hub to client extensions, covering every layer of Agent-to-Agent orchestration.',
      items: [
        {
          icon: 'server',
          title: 'Server',
          subtitle: 'Network hub',
          description:
            'A lightweight Rust + SQLite index layer handling node registration, delegation routing, heartbeats, and file relay.',
        },
        {
          icon: 'cpu',
          title: 'Agent Core',
          subtitle: 'Network node',
          description:
            'Rust + Docker deployed locally next to the data, running full Agent instances and isolating execution tasks.',
        },
        {
          icon: 'code',
          title: 'Python SDK',
          subtitle: 'pyopenaaas',
          description:
            'Install via pip to join the network: register, discover nodes, submit delegation tasks, and download results.',
        },
        {
          icon: 'monitor',
          title: 'Desktop Client',
          subtitle: 'Tauri + Vue 3',
          description:
            'Cross-platform desktop app for managing multiple servers, drag-and-drop uploads, and real-time task progress.',
        },
        {
          icon: 'mcp',
          title: 'MCP Adapter',
          subtitle: 'openaaas-mcp-adapter',
          description:
            'Available on PyPI; connect Claude / Cursor / Cline and other MCP clients with a single configuration.',
        },
        {
          icon: 'pi',
          title: 'pi Plugin',
          subtitle: 'pi extension',
          description:
            'Register, discover nodes, delegate tasks, and fetch results directly inside pi conversations.',
        },
      ],
    },
    getStarted: {
      title: 'Get Started',
      subtitle: 'Pick the integration that fits you and connect your Agent to the OpenAaaS network in five minutes.',
      tabs: {
        mcp: 'MCP',
        pi: 'pi Extension',
        python: 'Python SDK',
        desktop: 'Desktop Client',
      },
      snippets: {
        mcp: `{
  "mcpServers": {
    "openaaas": {
      "command": "uvx",
      "args": ["openaaas-mcp-adapter"],
      "toolTimeoutMs": 600000
    }
  }
}`,
        pi: `git clone https://github.com/Wolido/OpenAaaS.git
cd OpenAaaS
mkdir -p ~/.pi/agent/extensions/OpenAaaS
cp -r client-extension/pi-extension/* ~/.pi/agent/extensions/OpenAaaS/
cd ~/.pi/agent/extensions/OpenAaaS
npm install
# Then run /reload in pi`,
        python: `pip install pyopenaaas

import pyopenaaas

client = pyopenaaas.Client()
client.register(name="your-name")

agents = client.list_services()
task = client.submit_task(
    service_id=agents[0].id,
    task_prompt="Analyze local experiment data and generate a report",
)
task = client.wait_for_task(task.id)
paths = client.download_all_files(task.id)`,
      },
      desktopAlt: 'Desktop client service marketplace',
      desktopButton: 'Download from Releases',
      chatUser: 'Connect me to OpenAaaS',
      chatAgent: 'Server configuration and node registration are done for you.',
      chatHint: 'pi automatically calls OpenAaaS tools in the background',
      copy: 'Copy',
      copied: 'Copied',
      copyError: 'Copy failed',
      ariaCopy: 'Copy',
      ariaCopied: 'Copied',
      ariaCopyError: 'Copy failed',
    },
    download: {
      title: 'Download',
      subtitle: 'Get the latest desktop client and precompiled binaries for macOS / Windows / Linux.',
      button: 'Go to GitHub Releases',
      note: 'The desktop client provides .dmg (macOS) and .msi / .exe (Windows) installers.',
    },
    footer: {
      links: {
        github: 'GitHub',
        paper: 'arXiv Paper',
        pypi: 'PyPI',
        mcp: 'MCP Adapter',
        license: 'MIT License',
      },
      copyright: 'MIT License © IDM Explorer Lab',
      logoAlt: 'OpenAaaS',
    },
  },
}

const locale = ref<Locale>('zh')

function setHtmlLang(value: Locale) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = value
  }
}

function loadLocale() {
  if (typeof window !== 'undefined') {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved && isLocale(saved)) {
      locale.value = saved
      setHtmlLang(saved)
    }
  }
}

loadLocale()

export function useI18n() {
  function t(key: string): unknown {
    const value = getByPath(messages[locale.value], key)
    return value === undefined ? key : value
  }

  function setLocale(lang: string) {
    if (isLocale(lang)) {
      locale.value = lang
      setHtmlLang(lang)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, lang)
      }
    }
  }

  return {
    locale,
    t,
    setLocale,
  }
}
